import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface Tour {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  target_page: string | null;
  created_at: string;
  updated_at: string;
}

interface TourStep {
  id: string;
  tour_id: string;
  step_order: number;
  target: string;
  content: string;
  placement: string;
  options: {
    action?: {
      type: 'dialog' | 'navigation' | 'click';
      target?: string;
      config?: any;
    };
  };
}

interface DraftStep {
  target: string;
  content: string;
  placement: string;
  step_order: number;
  action_type: string;
  action_target: string;
  selected_dialog: string;
}

interface TourBuilderContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  currentTour: Tour | null;
  setCurrentTour: (tour: Tour | null) => void;
  isElementPickerActive: boolean;
  setIsElementPickerActive: (active: boolean) => void;
  selectedElement: string | null;
  setSelectedElement: (element: string | null) => void;
  currentPage: string;
  previewMode: boolean;
  setPreviewMode: (mode: boolean) => void;
  editingStep: TourStep | null;
  setEditingStep: (step: TourStep | null) => void;
  draftStep: DraftStep | null;
  setDraftStep: (draft: DraftStep | null) => void;
  shouldOpenStepEditor: boolean;
  setShouldOpenStepEditor: (should: boolean) => void;
}

const TourBuilderContext = createContext<TourBuilderContextType | undefined>(undefined);

export const useTourBuilder = () => {
  const context = useContext(TourBuilderContext);
  if (!context) {
    throw new Error('useTourBuilder must be used within a TourBuilderProvider');
  }
  return context;
};

interface TourBuilderProviderProps {
  children: React.ReactNode;
}

export const TourBuilderProvider: React.FC<TourBuilderProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentTour, setCurrentTour] = useState<Tour | null>(null);
  const [isElementPickerActive, setIsElementPickerActive] = useState(false);
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [editingStep, setEditingStep] = useState<TourStep | null>(null);
  const [draftStep, setDraftStep] = useState<DraftStep | null>(null);
  const [shouldOpenStepEditor, setShouldOpenStepEditor] = useState(false);
  
  const location = useLocation();
  const currentPage = location.pathname.slice(1) || 'dashboard';

  // Keep element picker active across page navigation
  // Only clear selectedElement when route changes
  useEffect(() => {
    setSelectedElement(null);
  }, [location.pathname]);

  const value: TourBuilderContextType = {
    isOpen,
    setIsOpen,
    currentTour,
    setCurrentTour,
    isElementPickerActive,
    setIsElementPickerActive,
    selectedElement,
    setSelectedElement,
    currentPage,
    previewMode,
    setPreviewMode,
    editingStep,
    setEditingStep,
    draftStep,
    setDraftStep,
    shouldOpenStepEditor,
    setShouldOpenStepEditor,
  };

  return (
    <TourBuilderContext.Provider value={value}>
      {children}
    </TourBuilderContext.Provider>
  );
};